/**
 * 三方对接供应商数据报表 — 配置与工具（对齐 cabinet/order 的 config.ts 职责）
 * PRD：docs/PRD-供应商报表.md
 */
import dayjs from 'dayjs'

export type StatPreset = 'LAST_7' | 'LAST_30' | 'CUSTOM'

export interface StatSearchModel {
  statRangePreset: StatPreset
  statDateStart: string
  statDateEnd: string
}

export function yesterday(): dayjs.Dayjs {
  return dayjs().subtract(1, 'day').startOf('day')
}

export function defaultSearchModel(): StatSearchModel {
  const end = yesterday()
  return {
    statRangePreset: 'LAST_7',
    statDateStart: end.subtract(6, 'day').format('YYYY-MM-DD'),
    statDateEnd: end.format('YYYY-MM-DD')
  }
}

export function daysInclusive(start: string, end: string): number {
  const a = dayjs(start).startOf('day')
  const b = dayjs(end).startOf('day')
  if (!a.isValid() || !b.isValid()) return 0
  return b.diff(a, 'day') + 1
}

export function buildRangePayload(model: StatSearchModel) {
  const preset = model.statRangePreset || 'LAST_7'
  const y = yesterday()

  if (preset === 'LAST_7') {
    return {
      statDateStart: y.subtract(6, 'day').format('YYYY-MM-DD') ,
      statDateEnd: y.format('YYYY-MM-DD')
    }
  }
  if (preset === 'LAST_30') {
    return {
      statDateStart: y.subtract(29, 'day').format('YYYY-MM-DD') ,
      statDateEnd: y.format('YYYY-MM-DD')
    }
  }

  return {
    statDateStart: model.statDateStart,
    statDateEnd: model.statDateEnd
  }
}

function formatCard(v: unknown, empty = '--'): string {
  if (v === null || v === undefined || v === '') return empty
  return String(v)
}

function formatPercent(v: unknown): string {
  if (v === null || v === undefined || v === '') return '--'
  const n = Number(v)
  if (Number.isNaN(n)) return '--'
  return `${n}%`
}

function formatDecimal2(v: unknown): string {
  if (v === null || v === undefined || v === '') return '--'
  const n = Number(v)
  if (Number.isNaN(n)) return '--'
  return n.toFixed(2)
}

export const METRICS_HELP_TEXT = `指标说明
1）投放量：统计时间内按发货时间落在统计窗内、且已实际发货的发货单计数。
2）入库量：统计时间内按入库时间落在统计窗内、且已实际入库的发货单计数。
3）实时店均格口占用量：截止今日 00:00，占用门店格口数量/占用门店数量；分母为零时展示为「--」。
4）累计门店格口占用量：统计窗内每日占用格口快照值之和。
5）累计占用格口时长：仅统计创建日晚于4月6日的发货单；按发货单分条统计；每条按小时向上取整再累加（小时）。
6）格口营销费用：累计门店格口占用量乘以单格口每日 0.5 元。
7）会员数量：当前供应商下会员状态为生效的三方会员人数（汇总）。
8）订阅取货率：完成领取会员礼包订单的去重用户数占全部会员去重用户数比例。
9）续订比例（满 30 天）：首次付费会员在满第 30 天观测点仍为有效会员的占比。
10）三方支付订单数：统计窗内渠道为三方开放平台且支付终态成功的订单笔数。`

export interface SummaryCardItem {
  title: string
  value: string
  sub: string
}

export function buildSummaryCards(s: Record<string, any>): SummaryCardItem[] {
  return [
    { title: '投放量', value: formatCard(s.sumLaunchCount), sub: '统计时间内完成发货的商品数量' },
    { title: '入库量', value: formatCard(s.sumInboundCount), sub: '统计时间内完成入库的商品数量' },
    {
      title: '实时店均格口占用量',
      value: formatDecimal2(s.latestShopAvgCellOccupy),
      sub: '截止今日 00:00，占用门店格口数量/占用门店数量'
    },
    {
      title: '累计门店格口占用量',
      value: formatCard(s.latestCumulativeCellOccupy),
      sub: '汇总统计时间内每日占用格口快照值'
    },
    {
      title: '累计占用格口时长（小时）',
      value: formatCard(s.latestCumulativeOccupyGridCount),
      sub: '按发货单分条统计，不足1小时按1小时统计'
    },
    {
      title: '格口营销费用（元）',
      value: formatDecimal2(s.latestCellMarketingCost),
      sub: '按单格口每日 0.5 元规则，以累计门店格口占用量计费'
    },
    {
      title: '会员数量',
      value: formatCard(s.latestThirdValidUserCount),
      sub: '会员状态为生效的会员人数汇总'
    },
    {
      title: '订阅取货率',
      value: formatPercent(s.latestThirdPickupRate),
      sub: '完成领取会员礼包订单的去重用户数占全部会员去重用户数比例'
    },
    {
      title: '续订比例（满 30 天）',
      value: formatPercent(s.latestRenewalRatio30d),
      sub: '首次付费会员在满第 30 日观测点仍为有效会员的占比'
    },
    {
      title: '三方支付订单数',
      value: formatCard(s.sumThirdOrderCount),
      sub: '统计窗内渠道为三方开放平台且支付终态成功的订单笔数'
    }
  ]
}


export const DailyTableColumns: any[] = [
  { prop: 'statDate', label: '统计日', minWidth: 110 },
  { prop: 'launchCount', label: '投放量', minWidth: 90 },
  { prop: 'inboundCount', label: '入库量', minWidth: 90 },
  { prop: 'inboundShipCount', label: '当日占用格口快照', minWidth: 130 },
  { prop: 'cumulativeCellOccupy', label: '累计门店格口占用量', minWidth: 150 },
  { prop: 'cumulativeOccupyGridCount', label: '累计占用格口时长（小时）', minWidth: 150 },
  { prop: 'cellMarketingCost', label: '格口营销费用（元）', minWidth: 140 },
  {
    prop: 'thirdPickupRate',
    label: '订阅取货率',
    minWidth: 110,
    formatter: (row: any) => {
      return formatPercent(row.thirdPickupRate)
    }
  },
  {
    prop: 'renewalRatio30d',
    label: '续订比例（满 30 天）',
    minWidth: 150,
    slot: 'percent',
    formatter: (row: any) => {
      return formatPercent(row.renewalRatio30d)
    }
  },
  { prop: 'thirdOrderCount', label: '三方支付订单数', minWidth: 130 }
]
